import dayjs from 'dayjs'
import { rrulestr } from 'rrule'

import { resolveTimezone } from './timezone'

// Keep parsed wall rules reusable during month navigation, as the vendor adapter
// does. Cache only wall dates; conversion to the current zone happens per call.
const rules = new Map<string, ReturnType<typeof rrulestr>>()
function parsedRule(source: string) {
  let parsed = rules.get(source)
  if (parsed) rules.delete(source)
  else parsed = rrulestr(source)
  if (rules.size >= 500) rules.delete(rules.keys().next().value!)
  rules.set(source, parsed)
  return parsed
}

/** Expand wall-clock recurrence dates without rrule's browser-zone conversion. */
export function expandZonedRecurrence(
  rule: string,
  from: Date,
  to: Date
): Date[] {
  const namedZone = rule.match(/TZID=([^:;\r\n]+)/)?.[1]
  if (!namedZone) return parsedRule(rule).between(from, to, true)

  const zone = resolveTimezone(namedZone)
  const floatingRule = rule
    .replace(/;TZID=[^:;\r\n]+/g, '')
    .replace(/UNTIL=(\d{8}T\d{6})Z?/g, (_match, value: string) => {
      const iso = `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}T${value.slice(9, 11)}:${value.slice(11, 13)}:${value.slice(13, 15)}Z`
      return `UNTIL=${dayjs(iso).tz(zone).format('YYYYMMDDTHHmmss')}`
    })
  // Local wall dates can be on either side of the UTC range. Filter by actual
  // instants after conversion, including offset changes within the range.
  const margin = 2 * 86400000
  return parsedRule(floatingRule)
    .between(new Date(from.valueOf() - margin), new Date(to.valueOf() + margin))
    .map((date) =>
      dayjs.tz(dayjs.utc(date).format('YYYY-MM-DD HH:mm:ss'), zone).toDate()
    )
    .filter((date) => date >= from && date <= to)
}
